import { useContext, useEffect, useRef, useState } from "react";
import './index.less';
import classNames from 'classnames';
import { ReactComponent as NoneSvg } from './icons/none.svg';
import { ReactComponent as SafeSvg } from './icons/safe.svg';
import { ReactComponent as EmergencySvg } from './icons/emergency.svg';
import { ReactComponent as VerySafeSvg } from './icons/very-safe.svg';
import { ReactComponent as FocusSvg } from './icons/focus.svg';
import { ReactComponent as EarlyWarnSvg } from './icons/early-warn.svg';
import { ReactComponent as RedDotSvg } from './icons/red-dot.svg';
import { ReactComponent as EmergencyLevelSvg } from './icons/emergency-level.svg';
import { ReactComponent as FocusLevelSvg } from './icons/focus-level.svg';
import { ReactComponent as WarningLevelSvg } from './icons/warning-level.svg';
import { ReactComponent as SafeLevelSvg } from './icons/safe-level.svg';
import { ReactComponent as EmergencyDotSvg } from './icons/emergency-dot.svg';
import { ReactComponent as FocusDotSvg } from './icons/focus-dot.svg';
import { ReactComponent as WarningDotSvg } from './icons/warning-dot.svg';
import { ReactComponent as SafeDotSvg } from './icons/safe-dot.svg';
import ReactECharts from 'echarts-for-react';
import WarnTable from "@/components/table/warn-table";
import { data, useParams } from "react-router-dom";
import { getProjectRankGroup, queryMobjects, queryProjectGlobalQuantities, queryQuantitiesMulti, queryQuantityCurrentDataList, queryQuantityGroupsMulti } from "@/declare/api/monix";
import dayjs from 'dayjs';
import { Transfer2DisplayScale } from "@/utils/unit";
import { ContextMenu } from "bimui";
import MonixCurvePanel from "./curve";
import { SystemContext } from "@/App";

// 对应的面板下标
enum WarnCategoryIndex {
    COMPONENT = 2,
    STRUCTURE,
    ENVIRONMENT,
}

// 对应的面板标题
const CategoryTitle = {
    [WarnCategoryIndex.STRUCTURE]: '结构',
    [WarnCategoryIndex.COMPONENT]: '构件',
    [WarnCategoryIndex.ENVIRONMENT]: '环境',
}

// 告警图例
enum WarnType {
    NONE = 0, // 无
    VERY_SAFE,   // 非常安全
    SAFE, // 安全
    FOCUS, // 重点关注
    EARLY_WARNING,  // 预警
    EMERGENCY,  // 紧急
}

const WarnTypeTitle = {
    [WarnType.VERY_SAFE]: '非常安全',
    [WarnType.SAFE]: '安全',
    [WarnType.FOCUS]: '重点关注',
    [WarnType.EARLY_WARNING]: '预警',
    [WarnType.EMERGENCY]: '紧急',
}

export const WarnTypeColor = {
    [WarnType.NONE]: '#c8c8c8',
    [WarnType.VERY_SAFE]: '#00944a',
    [WarnType.SAFE]: '#9EDBF9',
    [WarnType.FOCUS]: '#FFEF79',
    [WarnType.EARLY_WARNING]: '#F6A24E',
    [WarnType.EMERGENCY]: '#FA4131',
}

const WarnRadioIcons = {
    [WarnType.EMERGENCY]: <EmergencySvg />,
    [WarnType.EARLY_WARNING]: <EarlyWarnSvg />,
    [WarnType.FOCUS]: <FocusSvg />,
    [WarnType.SAFE]: <SafeSvg />,
    [WarnType.VERY_SAFE]: <VerySafeSvg />,
    [WarnType.NONE]: <NoneSvg />,
}

export const WarnLevelIcons = {
    [WarnType.EMERGENCY]: <EmergencyLevelSvg />,
    [WarnType.EARLY_WARNING]: <WarningLevelSvg />,
    [WarnType.FOCUS]: <FocusLevelSvg />,
    [WarnType.SAFE]: <SafeLevelSvg />,
    [WarnType.VERY_SAFE]: <></>,
    [WarnType.NONE]: <></>,
}

export const WarnDotIcons = {
    [WarnType.EMERGENCY]: <EmergencyDotSvg />,
    [WarnType.EARLY_WARNING]: <WarningDotSvg />,
    [WarnType.FOCUS]: <FocusDotSvg />,
    [WarnType.SAFE]: <SafeDotSvg />,
    [WarnType.VERY_SAFE]: <></>,
    [WarnType.NONE]: <></>,
}


// 指标类型
export const QuantityLevelTypes = {
    SENSOR_SQ: 1,
    SENSOR_MQ: 1,
    DIRECT_Q: 2,
    OTHER_Q: 3,
}

export default function WarnPanel() {
    const systemContext = useContext(SystemContext);

    const { monixProjectId } = useParams();

    const [showWarnIdx, setShowWarnIdx] = useState<WarnCategoryIndex>(WarnCategoryIndex.STRUCTURE);  // 当前显示的面板
    const showWarnIdxRef = useRef<WarnCategoryIndex>(WarnCategoryIndex.STRUCTURE);
    const [dataSource, setDataSource] = useState<any[]>([]);  // 报警表格的数据
    const [pieDatas, setPieDatas] = useState<any[]>([]);  // 饼图数据
    const [datas, setDatas] = useState<{}>({});
    const [icons, setIcons] = useState<any[]>([]);  // 所有面板展示的图表

    const [ctxDatas, setCtxDatas] = useState<any[]>([]);  // 点击图例出现弹窗的数据
    const [ctxOpen, setCtxOpen] = useState<boolean>(false);  // 是否显示弹窗
    const [ctxLeft, setCtxLeft] = useState<number>(0);  // 弹窗位置
    const [ctxTop, setCtxTop] = useState<number>(0);  // 弹窗位置
    const intervalRef = useRef(null); // 定时器
    const isLoadingRef = useRef(false); // 是否正在加载数据
    const datasRef = useRef(null);

    useEffect(() => {
        return () => {
            if (!!intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }
    }, [])

    useEffect(()=>{
        datasRef.current = datas;
    },[datas])

    useEffect(() => {
        setDataSource([]);
        setDatas({});
        if (!!intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        intervalRef.current = null;
        if (!monixProjectId) {
            return;
        }
        generateData();
        intervalRef.current = setInterval(() => {
            generateData(true);
        }, 30 * 1000);
    }, [monixProjectId])

    useEffect(() => {
        showWarnIdxRef.current = showWarnIdx;
        setDataSource([]);
        if (datas == null || Object.keys(datas).length === 0) {
            return;
        }
        const list = datas[showWarnIdx];
        if (!list) {
            return;
        }
        setDataSource(list);
    }, [datas, showWarnIdx])

    useEffect(() => {
        if (!monixProjectId) return;
        getProjectRankGroup(+monixProjectId).then(res => {
            const map = res.data || {};
            let icons = [];
            Object.keys(map).forEach(key => {
                let value = map[key];
                if (value === WarnType.NONE) {
                    value = WarnType.VERY_SAFE;
                }
                icons[key] = WarnRadioIcons[value];
            })
            setIcons([...icons]);
        })
    }, [monixProjectId])

    useEffect(() => {
        if (!dataSource || dataSource.length === 0) {
            setPieDatas([]);
            return;
        }
        // 根据dataSource数组中的current_grade分组统计比例
        const data = dataSource.reduce((acc: any[], cur: any) => {
            let grade = cur.current_grade;
            const idx = acc.findIndex((item: any) => item.grade === grade);
            if (idx === -1) {
                acc.push({ grade, count: 1 });
            } else {
                acc[idx].count++;
            }
            return acc;
        }, []);
        setPieDatas(data);
    }, [dataSource])

    // 点击切换当前告警类型
    const changeWarnIdx = (idx: WarnCategoryIndex) => {
        setShowWarnIdx(idx);
    }

    // 处理告警的图标
    const handleRadioIcon = (idx) => {
        if (idx !== showWarnIdx) {
            let oldIcon = icons[idx];
            if (oldIcon == null) oldIcon = WarnRadioIcons[WarnType.NONE];
            return oldIcon;
        }
        let maxGrade = pieDatas.reduce((max: number, cur: any) => cur.grade > max ? cur.grade : max, 0);
        icons[idx] = WarnRadioIcons[maxGrade];
        setIcons([...icons]);
        return WarnRadioIcons[maxGrade];
    }

    // 设置饼图
    const getPieOptions = {
        series: [
            {
                name: '报警统计',
                type: 'pie',
                radius: ['40%', '55%'],
                avoidLabelOverlap: false,
                label: {
                    show: true,
                    formatter: '{d}%',   // 只显示百分比
                    fontSize: 14,        // 文字大小
                    color: '#fff',       // 文字颜色
                },
                minAngle: 5,
                itemStyle: {
                    color: function (params) {
                        return WarnTypeColor[params.data.type];
                    }
                },
                data: pieDatas.map(pieData => {
                    return { value: pieData.count, type: pieData.grade };
                })
            }
        ]
    }

    // 获取监测数据
    const generateData = async (isInterval = false) => {
        if (isLoadingRef.current) return;
        isLoadingRef.current = true;
        // 查询监测对象列表
        let res;
        if (isInterval) {
            res = await queryMobjects({ project_id: monixProjectId, type: showWarnIdxRef.current });
        } else {
            res = await queryMobjects({ project_id: monixProjectId });
        }
        if (!res || res.status !== 200 || res.data == null || res.data.length === 0) {
            isLoadingRef.current = false;
            return;
        }

        // 获取全局指标
        const gQuantitiesRes = await queryProjectGlobalQuantities(+monixProjectId);
        // 根据每一个监测对象查询

        let mObjectIds = [];
        let mObjects = [];
        let mObjectMap = {};
        let mObjTypeMap = {};
        res.data.forEach(item => {
            mObjectIds.push(item.id);
            mObjects.push(item);
            mObjectMap[item.id] = item.name;
            mObjTypeMap[item.id] = item.type;
        });
        const quantityGroupRes = await queryQuantityGroupsMulti(mObjectIds);
        let groups = []; // 存储group
        let constagesList = [];

        let paramsList = [];
        for (let i = 0; i < res.data.length; i++) {
            const mobjectObj = res.data[i];
            let quantityGroups = quantityGroupRes.data[i] || [];
            quantityGroups = quantityGroups.sort((a, b) => b.level - a.level);
            let constages = [...(mobjectObj?.constages || [])];
            constages.sort((a, b) => dayjs(a.start_time).valueOf() - dayjs(b.start_time).valueOf());
            let group = quantityGroups.length > 0 ? quantityGroups[0] : null;
            if (!group) continue;
            constagesList.push(constages);

            paramsList.push({
                mobject_id: group.mobject_id,
                group_id: group.id,
                activated: true
            })
            groups.push(group);
        }
        const quantitiesRes = await queryQuantitiesMulti(+monixProjectId, paramsList);
        let quantitiesList = [];
        let qidList = [];
        for (let i = 0; i < quantitiesRes.data.length; i++) {
            let quantities = quantitiesRes.data[i] || [];
            quantities = quantities.filter((q) => q.level !== QuantityLevelTypes.DIRECT_Q);
            if (!quantities || quantities.length === 0) continue;
            quantities = quantities.map(item => Object.assign({}, item));
            let constages = constagesList[i];
            quantities.forEach((q) => {
                q.monitor_object_name = mObjectMap[q.monitor_object_id];
                q.constages = constages;
                let gQuantity = gQuantitiesRes.data.find(item => item.type === q.type);
                if (gQuantity) {
                    q.chinese_name = gQuantity.chinese_name;
                    q.unit_type = gQuantity.unit_type;
                    q.precision = gQuantity.precision;
                    let scale = Transfer2DisplayScale(q.unit_type);
                    q.scale = scale;
                    q.valid_data = gQuantity.valid_data;
                }
            })
            let qids = quantities.map(item => item.id) || [];
            qidList.push(...qids);
            quantitiesList.push(...quantities);
        }
        if (qidList.length === 0 || quantitiesList.length === 0) {
            isLoadingRef.current = false;
            return;
        }
        let queryQuantityCurrentDataPromiseRes = await queryQuantityCurrentDataList(qidList)
        let dataList = queryQuantityCurrentDataPromiseRes.data || [];
        dataList.forEach(item => {
            if (!item) {
                isLoadingRef.current = false;
                return;
            }
            let id = +item['id'];
            let value = +item['value'];
            let q = quantitiesList.find(qs => qs.id === id);
            q.current_value = value * q.scale;
            if (!q.is_evaluate) {
                q.current_grade = 0;
            } else {
                q.current_grade = checkQuantityEvaluation(q.warning_limit, value, q.constages);
            }
        });
        quantitiesList = quantitiesList.sort((a, b) => b.current_grade - a.current_grade);
        let dataMap = {};
        quantitiesList.forEach(q => {
            if (q.current_grade === WarnType.NONE) q.current_grade = WarnType.VERY_SAFE;
            let mObjType = mObjTypeMap[q.monitor_object_id];
            if (!!mObjType) {
                if (!dataMap[mObjType]) {
                    dataMap[mObjType] = [];
                }
                dataMap[mObjType].push(q);
            }
        })
        if (!isInterval) {
            setDatas(dataMap);
        } else {
            setDatas({ ...datasRef.current, ...dataMap });
        }
        isLoadingRef.current = false;
    }

    // 获取警告等级
    const checkQuantityEvaluation = (warningLimits, value: number, constages) => {
        let time = dayjs().valueOf();
        if (constages.length === 0) return 0;
        let stage = constages.find((item) => dayjs(item.start_time).valueOf() <= time && dayjs(item.end_time).valueOf() >= time);
        if (!stage) stage = constages[constages.length - 1];
        let limit = warningLimits.find((item) => item.stage_id === stage.id)?.values;
        if (!limit)
            return 0;
        if (Math.abs(limit['t5']) < 1e-9)
            return 0;
        if (!value)
            return 0;
        if (limit['t5'] <= Math.abs(value)) {
            return 5;
        } else if (limit['t4'] <= Math.abs(value)) {
            return 4;
        } else if (limit['t3'] <= Math.abs(value)) {
            return 3;
        } else if (limit['t2'] <= Math.abs(value)) {
            return 2;
        } else {
            return 1;
        }
    }

    // 点击报警图例，出现弹窗
    const onClickWarnType = (type: WarnType) => {
        const gradeDatas = dataSource.filter(data => data.current_grade === type);
        // 遍历拿到mObjectId
        let mObjectIdSet = new Set();
        gradeDatas.forEach(data => {
            mObjectIdSet.add(data.mobject_id);
        })
        // 遍历datasource，筛选出mObjectId在mObjectIdSet中的数据
        const ctxDatas = dataSource.filter(data => mObjectIdSet.has(data.monitor_object_id));
        const doc = document.getElementById(`warn-type-${type}`);
        if (!doc) return;
        const rect = doc.getBoundingClientRect();
        setCtxDatas([...ctxDatas]);
        setCtxOpen(true);
        setCtxLeft(rect.left);
        setCtxTop(rect.top);
    }


    return <div className={classNames({
        "warn-panel": true,
        'component': showWarnIdx === WarnCategoryIndex.COMPONENT,
        'structure': showWarnIdx === WarnCategoryIndex.STRUCTURE,
        'environment': showWarnIdx === WarnCategoryIndex.ENVIRONMENT,
    })}>
        <div className="left-category">
            {Object.keys(CategoryTitle).map(idx => {
                return <div className={classNames({
                    'category-item': true,
                    'active': showWarnIdx === +idx
                })} onClick={() => changeWarnIdx(+idx)}>
                    <span className="radio">
                        {handleRadioIcon(idx)}
                    </span>
                    <span className='category-title'>{CategoryTitle[idx]}</span>
                </div>
            })}
        </div>
        <div className="mid-warn">
            <div className="mid-warn-title">
                <span>报警统计</span>
            </div>
            <div className="warn-pie">
                <ReactECharts option={getPieOptions} style={{ height: 107, width: 300, flexShrink: 0 }} />
                <div className="legend">
                    {Object.keys(WarnTypeTitle).map(idx => {
                        return <div className="warn-type-item" onClick={() => onClickWarnType(+idx)} key={`warn-type-${idx}`} id={`warn-type-${idx}`}>
                            <span className="warn-type-color" style={{ backgroundColor: WarnTypeColor[+idx] }} />
                            <span className="warn-type-title">{WarnTypeTitle[+idx]}</span>
                        </div>
                    })}
                </div>
            </div>
            <div className="warn-table">
                <WarnTable dataSource={dataSource} />
            </div>
        </div>
        {/* {systemContext.activeMonitorObjectId != null && <MonixCurvePanel />} */}
        <MonixCurvePanel />
        <div className='divider'></div>
        <ContextMenu wrapperClassname="glass-menu ctx-data-menu" visible={ctxOpen} left={ctxLeft} top={ctxTop}
            onCancel={() => setCtxOpen(false)} width={104}>
            {ctxDatas.map((ctxData, index) => {
                return <div className="ctx-data-row" key={`ctx-data-${index}`}>
                    <span className="ctx-data-row-icon">
                        <RedDotSvg />
                    </span>
                    <span title={`${ctxData.name}`} className="ctx-data-row-name ellipsis">{ctxData.name}</span>
                </div>
            })}
        </ContextMenu>
    </div>
}